/**
 * Пиксели сплошной обводки по силуэту картинки: евклидово расстояние до
 * краевых пикселей, круглая кисть. Контур идет точно по непрозрачной форме
 * источника; внутри самого силуэта пусто — поверх рисуется исходный спрайт.
 *
 * Чистая функция над RGBA-массивом — без канвасов и DOM, чтобы результат
 * можно было держать в памяти (см. generateOutlineTexture) и проверять тестом.
 *
 * @param {Uint8ClampedArray|Uint8Array} srcData - RGBA источника, построчно сверху вниз
 * @param {number} srcWidth
 * @param {number} srcHeight
 * @param {number} thickness - толщина обводки в пикселях
 * @param {number} color - цвет обводки, 0xRRGGBB
 * @returns {{data: Uint8Array, width: number, height: number, pad: number}|null}
 *   RGBA обводки (построчно сверху вниз) с отступом pad со всех сторон, или
 *   null, если у источника нет ни одного непрозрачного края: пустой источник
 *   (картинка еще не прочиталась) дал бы пустую обводку, и ее нельзя кэшировать
 */
export const computeOutlinePixels = (srcData, srcWidth, srcHeight, thickness, color) => {
  if (!srcData || srcWidth <= 0 || srcHeight <= 0) {
    return null;
  }

  // Отступ: круглая обводка не обрезается даже у самого края картинки
  const pad = Math.max(thickness + 8, 50);
  const outWidth = srcWidth + pad * 2;
  const outHeight = srcHeight + pad * 2;
  // Новый массив уже заполнен нулями — полностью прозрачный
  const outData = new Uint8Array(outWidth * outHeight * 4);

  const r = (color >> 16) & 0xFF;
  const g = (color >> 8) & 0xFF;
  const b = color & 0xFF;
  // Круглая кисть построчно: полуширина на каждом смещении по вертикали
  const brushHalfWidths = Array.from({length: thickness * 2 + 1}, (_, index) => {
    const dy = index - thickness;
    return Math.floor(Math.sqrt(thickness * thickness - dy * dy));
  });
  const brushMask = new Uint8Array(outWidth * outHeight);
  let hasEdge = false;
  const isOpaque = (idx) => srcData[idx + 3] > 10;

  // 1. Краевые пиксели: непрозрачные, касающиеся прозрачного соседа или края картинки
  for (let y = 0; y < srcHeight; y++) {
    for (let x = 0; x < srcWidth; x++) {
      if (!isOpaque((y * srcWidth + x) * 4)) continue;

      let isEdge = false;
      for (let dy = -1; dy <= 1 && !isEdge; dy++) {
        for (let dx = -1; dx <= 1 && !isEdge; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || nx >= srcWidth || ny < 0 || ny >= srcHeight) {
            isEdge = true;
          } else if (!isOpaque((ny * srcWidth + nx) * 4)) {
            isEdge = true;
          }
        }
      }

      if (isEdge) {
        hasEdge = true;
        // Кисть вокруг краевого пикселя: так не нужно искать соседей для каждого
        // пикселя результата, а это сотни миллисекунд на иконку отряда
        const centerX = x + pad;
        const centerY = y + pad;
        brushHalfWidths.forEach((halfWidth, index) => {
          const row = (centerY + index - thickness) * outWidth;
          brushMask.fill(1, row + centerX - halfWidth, row + centerX + halfWidth + 1);
        });
      }
    }
  }

  if (!hasEdge) {
    return null;
  }

  // 2. Результат: под кистью, но не внутри непрозрачного силуэта — поверх рисуется сам отряд
  for (let y = 0; y < outHeight; y++) {
    for (let x = 0; x < outWidth; x++) {
      if (!brushMask[y * outWidth + x]) continue;

      const srcX = x - pad;
      const srcY = y - pad;
      if (srcX >= 0 && srcX < srcWidth && srcY >= 0 && srcY < srcHeight
        && isOpaque((srcY * srcWidth + srcX) * 4)) {
        continue;
      }

      const outIdx = (y * outWidth + x) * 4;
      outData[outIdx] = r;
      outData[outIdx + 1] = g;
      outData[outIdx + 2] = b;
      outData[outIdx + 3] = 255;
    }
  }

  return {data: outData, width: outWidth, height: outHeight, pad};
};
